import { Box, Divider, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getResultSummary,
  getEdgeBuriedDepth,
} from "../helperFunctions/resultMetrics";

// Formats a number with up to 2 decimals; blank for null/undefined.
const fmt = (v, digits = 2) =>
  v == null || Number.isNaN(v)
    ? "–"
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });

// One "min / ø / max" stat row.
const StatRow = ({ label, stat, unit }) => (
  <Box className="flex flex-row justify-between items-baseline gap-3">
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
      {stat
        ? `${fmt(stat.min)} / ${fmt(stat.avg)} / ${fmt(stat.max)} ${unit}`
        : "–"}
    </Typography>
  </Box>
);

// Two-value mini bar comparison (e.g. first vs final year flow).
const MiniBars = ({ items, unit }) => {
  const max = Math.max(...items.map((i) => i.value || 0), 0.0001);
  return (
    <Box className="flex flex-col gap-1">
      {items.map((i) => (
        <Box key={i.label} className="flex flex-row items-center gap-2">
          <Typography variant="caption" sx={{ width: 64, flexShrink: 0 }}>
            {i.label}
          </Typography>
          <Box
            sx={{
              flex: 1,
              height: 8,
              borderRadius: "4px",
              background: "rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${((i.value || 0) / max) * 100}%`,
                height: "100%",
                background: "hsl(210, 85%, 45%)",
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{ width: 56, flexShrink: 0, textAlign: "right" }}
          >
            {fmt(i.value)} {unit}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Floating result-view panel summarizing the optimization result: element
// counts, total pipe length, diameter distribution, buried depth and final-year
// flow, plus per-edge details when an edge is selected.
export const ResultSummaryPanel = ({ elements, selectedElement }) => {
  const { t } = useTranslation();
  const summary = useMemo(() => getResultSummary(elements), [elements]);

  const selectedEdge =
    selectedElement && selectedElement.type === "edge"
      ? elements.find((e) => e.id === selectedElement.id)
      : null;

  const diameterMax = Math.max(
    ...summary.diameterCounts.map((d) => d.count),
    1
  );

  const km = summary.totalLength / 1000;

  return (
    <Box
      className="absolute left-24 bottom-24 flex flex-col gap-2 p-3 rounded"
      sx={{
        zIndex: 9998,
        width: 260,
        maxHeight: "80vh",
        overflowY: "auto",
        backgroundColor: "rgba(255,255,255,0.94)",
        boxShadow: 3,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {t("Metrics")}
      </Typography>

      {/* element counts + total length */}
      <Box className="flex flex-row justify-between">
        <Typography variant="caption">{t("Edges")}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {summary.edgeCount}
        </Typography>
      </Box>
      <Box className="flex flex-row justify-between">
        <Typography variant="caption">{t("Nodes")}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {summary.nodeCount}
        </Typography>
      </Box>
      <Box className="flex flex-row justify-between">
        <Typography variant="caption">{t("Total pipe length")}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {km >= 1 ? `${fmt(km)} km` : `${fmt(summary.totalLength)} m`}
        </Typography>
      </Box>

      {!summary.hasResult && (
        <Typography variant="caption" sx={{ fontStyle: "italic", mt: 1 }}>
          {t("Optimize first to see the results")}
        </Typography>
      )}

      {summary.hasResult && (
        <>
          <Divider />
          {/* diameter distribution histogram */}
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {t("Diameter")} ({t("distribution")})
          </Typography>
          <Box className="flex flex-col gap-1">
            {summary.diameterCounts.map((d) => (
              <Box key={d.value} className="flex flex-row items-center gap-2">
                <Typography
                  variant="caption"
                  sx={{ width: 48, flexShrink: 0 }}
                >
                  {fmt(d.value)} m
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: "4px",
                    background: "rgba(0,0,0,0.08)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${(d.count / diameterMax) * 100}%`,
                      height: "100%",
                      background: "hsl(210, 85%, 45%)",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ width: 28, flexShrink: 0, textAlign: "right" }}
                >
                  {d.count}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider />
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, opacity: 0.7 }}
          >
            {t("min / ø / max")}
          </Typography>
          <StatRow
            label={t("Buried depth")}
            stat={summary.buriedDepth}
            unit="m"
          />
          <StatRow
            label={t("Flow (final year)")}
            stat={summary.flowFinal}
            unit="l/s"
          />
        </>
      )}

      {selectedEdge && (
        <>
          <Divider />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t("Selected edge")}
            {selectedEdge.name ? `: ${selectedEdge.name}` : ""}
          </Typography>
          {(() => {
            const getCalc = (key) =>
              selectedEdge.calc?.find((p) => p.key === key)?.value;
            const getProp = (key) =>
              selectedEdge.props?.find((p) => p.key === key)?.value;
            const buried = getEdgeBuriedDepth(selectedEdge, elements);
            return (
              <Box className="flex flex-col gap-1">
                <Box className="flex flex-row justify-between">
                  <Typography variant="caption">{t("Diameter")}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {fmt(getCalc("diameter"))} m
                  </Typography>
                </Box>
                <Box className="flex flex-row justify-between">
                  <Typography variant="caption">{t("Length")}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {fmt(getProp("length"))} m
                  </Typography>
                </Box>
                <Box className="flex flex-row justify-between">
                  <Typography variant="caption">
                    {t("Buried depth")}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {fmt(buried)} m
                  </Typography>
                </Box>
                <MiniBars
                  unit="l/s"
                  items={[
                    {
                      label: t("First year"),
                      value: Number(getCalc("first_year_pipe_flow")) || 0,
                    },
                    {
                      label: t("Final year"),
                      value: Number(getCalc("final_year_pipe_flow")) || 0,
                    },
                  ]}
                />
              </Box>
            );
          })()}
        </>
      )}
    </Box>
  );
};
